import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Stack,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  useMediaQuery,
} from '@mui/material';

const TIMEZONES = ['Europe/Warsaw', 'Europe/London', 'Europe/Berlin', 'UTC'];
const LANGUAGES = [
  { value: 'pl', label: 'Polski' },
  { value: 'en', label: 'English' },
];

const STEPS = ['Dane organizacji', 'Region i język', 'Regulamin'];

function Onboarding() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeStep, setActiveStep] = useState(0);
  const [form, setForm] = useState({
    name: '',
    email: '',
    timezone: 'Europe/Warsaw',
    language: 'pl',
    regulations: '',
  });

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleNext = (e) => {
    e.preventDefault();
    if (activeStep === STEPS.length - 1) {
      navigate('/import');
    } else {
      setActiveStep((s) => s + 1);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: { xs: 2, sm: 3 },
      }}
    >
      <Card sx={{ width: '100%', maxWidth: 560, borderRadius: 4 }}>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Typography variant="h5" fontWeight={700} align="center" gutterBottom>
            Skonfiguruj organizację
          </Typography>
          <Stepper
            activeStep={activeStep}
            orientation={isMobile ? 'vertical' : 'horizontal'}
            sx={{ my: 3 }}
          >
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <form onSubmit={handleNext}>
            <Stack spacing={2.5}>
              {activeStep === 0 && (
                <>
                  <TextField
                    label="Nazwa organizacji"
                    fullWidth
                    required
                    value={form.name}
                    onChange={handleChange('name')}
                  />
                  <TextField
                    label="E-mail do powiadomień"
                    type="email"
                    fullWidth
                    required
                    value={form.email}
                    onChange={handleChange('email')}
                  />
                </>
              )}

              {activeStep === 1 && (
                <>
                  <FormControl fullWidth required>
                    <InputLabel id="timezone-label">Strefa czasowa</InputLabel>
                    <Select
                      labelId="timezone-label"
                      value={form.timezone}
                      label="Strefa czasowa"
                      onChange={handleChange('timezone')}
                    >
                      {TIMEZONES.map((tz) => (
                        <MenuItem key={tz} value={tz}>
                          {tz}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth required>
                    <InputLabel id="language-label">Język</InputLabel>
                    <Select
                      labelId="language-label"
                      value={form.language}
                      label="Język"
                      onChange={handleChange('language')}
                    >
                      {LANGUAGES.map((lang) => (
                        <MenuItem key={lang.value} value={lang.value}>
                          {lang.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </>
              )}

              {activeStep === 2 && (
                <TextField
                  label="Regulamin organizacji"
                  multiline
                  rows={6}
                  fullWidth
                  required
                  value={form.regulations}
                  onChange={handleChange('regulations')}
                  helperText="Pracownicy zaakceptują ten regulamin przy dołączaniu do kampanii."
                />
              )}

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'space-between',
                  gap: 1.5,
                  pt: 2,
                }}
              >
                <Button
                  disabled={activeStep === 0}
                  onClick={() => setActiveStep((s) => s - 1)}
                  sx={{ order: { xs: 2, sm: 1 }, width: { xs: '100%', sm: 'auto' } }}
                >
                  Wstecz
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  sx={{ order: { xs: 1, sm: 2 }, width: { xs: '100%', sm: 'auto' } }}
                >
                  {activeStep === STEPS.length - 1 ? 'Zakończ onboarding' : 'Dalej'}
                </Button>
              </Box>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
}

export default Onboarding;
